import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ButtonComponent } from '@ppl/ui';
import { Paginator, PaginatorState } from 'primeng/paginator';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { SectionsService } from '../../../../services/sections.service';
import { PAGE_CONSTANTS } from '../../../../shared/constants/page.constants';
import { ClaimsService } from '../../../../services/claims.service';
import { UserService } from '../../../../services/user.service';
import { AuthorizeButtonDirective } from '../../../../shared/authorization/authorizeButton.directive';
import { AllowListAuthStore } from '../../../../shared/authorization/allow-list-auth.store';
import { ListService } from '../../../../shared/services/list.service';
import { ListGridRequestPayload, SearchParams } from '../../../../shared/interfaces/list.interface';
import { AuthorizationsDetailsService } from '../../../../services/authorizations-service.service';
import { formatServicePrice, getServiceFrequencyLabel } from '../../../../shared/utils/auth-service-detail.util';
import { DateTimeFormatterService } from '../../../../shared/utils/date-time-formatter.service';

/** One row of the `spending-plan-summary` list response. */
interface SpendingPlanSummary {
  clientAuthName: string;
  participantName: string;
  pplId: string;
  uciId: string;
  authUnit: number;
  planStartDate: string;
  planEndDate: string;
  serviceCoordinator: string | null;
  regionalCenter: string | null;
  modifiedDate: string;
}

/** One service detail of a provider allocation, as `spendingPlanDetails` returns it. */
interface ServiceDetailRecord {
  authProviderServiceId?: number;
  serviceDetail?: string;
  frequency?: any;
  units?: any;
  price?: any;
  isActive?: boolean;
}

/** One provider allocation of an authorization, as `spendingPlanDetails` returns it. */
interface ProviderAllocationRecord {
  authorizationProviderId?: number;
  providerId?: number | null;
  providerPplId?: string | null;
  providerName?: string;
  startDate?: string;
  endDate?: string;
  providerAllocatedDollar?: any;
  providerUsedDollar?: any;
  providerRemainingDollar?: any;
  isActive?: boolean;
  serviceDetails?: ServiceDetailRecord[];
}

/** One authorization of a spending plan, as `spendingPlanDetails` returns it. */
interface AuthorizationRecord {
  tasAuthorizationDetailId?: number;
  serviceCode?: string;
  serviceCodeDescription?: string;
  serviceDescription?: string;
  sourceValue?: string;
  fromDate?: string;
  toDate?: string;
  authorizedDollar?: any;
  spentDollar?: any;
  remainingDollar?: any;
  statusLkp?: string;
  isDeleted?: any;
  authProviders?: ProviderAllocationRecord[];
}

/** Authorization-level cell, rendered once per authorization via rowspan. */
interface AuthCell {
  authorizationId: number;
  codeDesc: string;
  serviceDescription: string;
  sourceValue: string;
  term: string;
  authorized: string;
  spent: string;
  remaining: string;
  isDeactivated: boolean;
}

/** Provider allocation cell, rendered once per allocation via rowspan. */
interface AllocationCell {
  authorizationProviderId: number | null;
  pplProviderId: string;
  providerName: string;
  term: string;
  allocated: string;
  used: string;
  remaining: string;
  isActive: boolean;
  isUnassigned: boolean;
  isPlaceholder: boolean;
}

/** Service detail cell — the leaf, one per physical row. */
interface ServiceDetailCell {
  serviceDetail: string;
  frequencyLabel: string;
  units: string;
  priceDisplay: string;
  isActive: boolean;
  isPlaceholder: boolean;
}

/**
 * One physical row of the detail table. `auth` and `allocation` are populated only on
 * the first row of their block; the rowspan values carry them down the rows beneath, so
 * each value is written once instead of repeating on every leaf.
 */
interface DetailRow {
  auth: AuthCell | null;
  authRowSpan: number;
  allocation: AllocationCell | null;
  allocationRowSpan: number;
  serviceDetail: ServiceDetailCell;
  /** Last service detail of its provider allocation — carries the closing rule. */
  isAllocationEnd: boolean;
}

/** One spending plan — the top-level, expandable row. */
interface SpendingPlanRow {
  clientAuth: string;
  consumerName: string;
  /** UCI ID — `uciId` on the summary response. */
  medicaid: string;
  consumerPPLId: string;
  serviceCoordinator: string;
  orgName: string;
  authorized: string;
  term: string;
  detailRows: DetailRow[];
  detailsLoaded: boolean;
  detailsLoading: boolean;
  detailsError: boolean;
  expanded: boolean;
}

/**
 * Spending plans list — spending plans with their authorizations, provider allocations
 * and service details in one list rather than a drill-down.
 *
 * Uses plain tables rather than `ppl-list-table`: that component supports a single flat
 * level of line items and projects no template into the expanded row, so the three
 * levels below a plan could only be shown by repeating every parent value on every leaf
 * row. Here the detail table groups them with rowspan instead — an authorization spanning
 * six service details is written once, not six times.
 *
 * The plan rows come from the `spending-plan-summary` list API, searched and paged
 * server-side. The expanded detail rows are still wireframe data — see `loadDetailRows`.
 */
@Component({
  selector: 'ppl-spending-plans-list',
  standalone: true,
  imports: [ButtonComponent, AuthorizeButtonDirective, Paginator],
  templateUrl: './spending-plans-list.component.html',
  styleUrl: './spending-plans-list.component.css',
})
export class SpendingPlansListComponent implements OnInit, OnDestroy {
  /** Permissions mirror the authorizations grid — same records, same access. */
  pageId: string = PAGE_CONSTANTS.AUTHORIZATION_DATA_GRID_PAGE;
  programName!: string;
  isPageRestrictionLoaded = false;
  @Input() isReadOnly = false;

  /** Column count of the plan table, for the detail row's colspan. */
  readonly planColumnCount = 9;

  private static readonly PAGE_NAME = 'spending-plan-summary';
  /**
   * The API ignores `recordsPerPage` and answers with 100 records per `startingPageNumber`,
   * so a batch is always 100 and the paginator slices it to the size the user picked. Every
   * page size on offer divides 100 evenly, so a displayed page never straddles two batches.
   */
  private static readonly SERVER_PAGE_SIZE = 100;
  /** Fields the toolbar search runs against, OR'd together server-side. */
  private static readonly SEARCH_FIELDS = [
    'clientAuthName',
    'participantName',
    'pplId',
    'uciId',
    'serviceCoordinator',
    'regionalCenter',
  ];

  /** The rows on screen — one paginator page, sliced out of a loaded batch. */
  plans: SpendingPlanRow[] = [];
  searchTerm = '';
  isLoading = false;
  hasError = false;

  // Paginator state. `first` is the zero-based offset into the whole result set.
  recordsPerPage = 20;
  readonly recordsPerPageOptions = [10, 20, 50, 100];
  first = 0;
  totalRecords = 0;
  /**
   * False when the API returns no `Count`/`TotalRecords`. One record beyond the loaded
   * ones is then claimed so the next page stays reachable, and the paginator report omits
   * the total rather than stating a number that isn't real.
   */
  hasKnownTotal = false;

  /** Loaded batches, keyed by 1-based `startingPageNumber`. */
  private readonly batches = new Map<number, SpendingPlanRow[]>();
  /** Records proven to exist — the end of the highest batch loaded so far. */
  private knownRecordCount = 0;
  /** True while the highest batch loaded came back full, so another may follow. */
  private mayHaveMore = false;
  /** Record count as reported by the API; only meaningful when `hasKnownTotal`. */
  private serverTotal = 0;
  private readonly searchInput$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private sectionsService: SectionsService,
    private claimsService: ClaimsService,
    private userService: UserService,
    private listService: ListService,
    private authorizationService: AuthorizationsDetailsService,
    private dateTimeFormatter: DateTimeFormatterService,
    private allowListStore: AllowListAuthStore
  ) { }

  ngOnInit() {
    this.sectionsService.setFlow('authorizations');
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    this.setPageAccess();

    this.searchInput$
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.fetchPlans());

    this.fetchPlans();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** The record count is only stated when the API actually returned one. */
  get pageReportTemplate(): string {
    return this.hasKnownTotal
      ? 'Showing {first} to {last} of {totalRecords} spending plans'
      : 'Showing {first} to {last}';
  }

  onAddAuthorizations() {
    if (this.isReadOnly) return;
    this.router.navigate(['/home/addAuthorizations']);
  }

  onViewAuthorizations() {
    this.router.navigate(['/home/authorizations']);
  }

  togglePlan(plan: SpendingPlanRow) {
    plan.expanded = !plan.expanded;
    if (plan.expanded && !plan.detailsLoaded) {
      this.loadDetailRows(plan);
    }
  }

  expandAll() {
    this.plans.forEach(plan => {
      plan.expanded = true;
      if (!plan.detailsLoaded) {
        this.loadDetailRows(plan);
      }
    });
  }

  collapseAll() {
    this.plans.forEach(plan => (plan.expanded = false));
  }

  /** Search runs server-side; debounced so a typed word is one request, not six. */
  onSearch(value: string) {
    this.searchTerm = value ?? '';
    this.searchInput$.next(this.searchTerm.trim());
  }

  /**
   * Page link or rows-per-page change. Served from the batch already in hand whenever the
   * requested offset falls inside it; otherwise the batch holding that offset is fetched,
   * so jumping straight to a far page lands on the right records.
   */
  onPageChange(event: PaginatorState) {
    this.recordsPerPage = event.rows ?? this.recordsPerPage;
    this.first = event.first ?? 0;

    const batch = this.batchNumberFor(this.first);
    if (!this.batches.has(batch)) {
      this.fetchPlans(batch);
      return;
    }

    this.showCurrentPage();
  }

  retry() {
    this.fetchPlans(this.batchNumberFor(this.first));
  }

  /** The 1-based `startingPageNumber` holding a given record offset. */
  private batchNumberFor(offset: number): number {
    return Math.floor(offset / SpendingPlansListComponent.SERVER_PAGE_SIZE) + 1;
  }

  /** View on an authorization block opens the existing authorization details page. */
  onViewAuthorization(auth: AuthCell) {
    if (!auth?.authorizationId) return;

    this.claimsService.clearAuthData();
    this.userService.setLeadEntry('authorizations');
    this.userService.setSelectedLead(String(auth.authorizationId));
    setTimeout(() => {
      this.router.navigate(['/home/authorizations/details/']);
    }, 200);
  }

  /**
   * Fetches one batch of 100. Batch 1 starts the list over — the first load, or a new
   * search — and later batches are cached alongside it.
   */
  private fetchPlans(batch = 1) {
    this.isLoading = true;
    this.hasError = false;

    this.listService
      .getListData(this.buildRequestPayload(batch), '')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          const records: SpendingPlanSummary[] = data?.responsedata ?? [];
          const rows = records.map(record => this.toSpendingPlanRow(record));

          if (batch === 1) {
            this.batches.clear();
            this.knownRecordCount = 0;
            this.first = 0;
          }

          this.isLoading = false;

          // The API ignores `recordsPerPage`; if it ignores `startingPageNumber` too, the
          // batch repeats batch 1. Treat that as the end of the data rather than paging
          // through the same records under different page numbers.
          if (batch > 1 && this.repeatsFirstBatch(rows)) {
            this.mayHaveMore = false;
            this.showCurrentPage();
            return;
          }

          this.batches.set(batch, rows);

          const batchStart = (batch - 1) * SpendingPlansListComponent.SERVER_PAGE_SIZE;
          this.knownRecordCount = Math.max(this.knownRecordCount, batchStart + rows.length);
          this.mayHaveMore = rows.length === SpendingPlansListComponent.SERVER_PAGE_SIZE;

          const total = this.parseTotal(data);
          this.hasKnownTotal = total !== undefined;
          this.serverTotal = total ?? 0;

          this.showCurrentPage();
        },
        error: () => {
          if (batch === 1) {
            this.batches.clear();
            this.knownRecordCount = 0;
            this.first = 0;
          }
          this.mayHaveMore = false;
          this.hasError = true;
          this.isLoading = false;
          this.showCurrentPage();
        },
      });
  }

  /** A plan is identified by its number and participant — the detail call's two inputs. */
  private planKey(plan: SpendingPlanRow): string {
    return `${plan.clientAuth}|${plan.consumerPPLId}`;
  }

  private repeatsFirstBatch(rows: SpendingPlanRow[]): boolean {
    const firstBatch = this.batches.get(1) ?? [];
    if (!rows.length || rows.length !== firstBatch.length) return false;
    return this.planKey(rows[0]) === this.planKey(firstBatch[0]);
  }

  /**
   * Recomputes the visible rows and the paginator total. When the record count is unknown,
   * one extra record is claimed while a further batch may exist, so the next page stays
   * reachable without inventing a count.
   */
  private showCurrentPage() {
    const pageSize = SpendingPlansListComponent.SERVER_PAGE_SIZE;

    this.totalRecords = this.hasKnownTotal
      ? Math.max(this.serverTotal, this.knownRecordCount)
      : this.knownRecordCount + (this.mayHaveMore ? 1 : 0);

    // A page size change, or a batch that ended earlier than expected, can leave `first`
    // past the end — drop back to the start of the last page that has rows.
    const lastPageStart = Math.floor(
      Math.max(this.knownRecordCount - 1, 0) / this.recordsPerPage
    ) * this.recordsPerPage;
    this.first = Math.min(this.first, lastPageStart);

    const batch = this.batches.get(this.batchNumberFor(this.first)) ?? [];
    const offsetInBatch = this.first % pageSize;
    this.plans = batch.slice(offsetInBatch, offsetInBatch + this.recordsPerPage);
  }

  private buildRequestPayload(page: number): ListGridRequestPayload {
    const searchParam = this.buildSearchParams();

    return {
      pageName: SpendingPlansListComponent.PAGE_NAME,
      programCode: this.programName,
      // Cross-field search only makes sense as an OR; with no search the default stands.
      condition: searchParam.length > 1 ? 'or' : 'and',
      recordsPerPage: SpendingPlansListComponent.SERVER_PAGE_SIZE,
      startingPageNumber: String(page),
      searchParam,
      orderByColumn: ['modifiedDate'],
      orderBy: ['desc'],
    };
  }

  private buildSearchParams(): SearchParams[] {
    const term = this.searchTerm.trim();
    if (!term) return [];

    return SpendingPlansListComponent.SEARCH_FIELDS.map(field => ({
      jsonPath: field,
      value: term,
      operator: 'Contains',
    }));
  }

  /** `Count` on the newer list responses, `TotalRecords` on the older ones. */
  private parseTotal(response: any): number | undefined {
    const value = response?.Count ?? response?.TotalRecords;
    const parsed = Number(value);
    return value !== null && value !== undefined && value !== '' && Number.isFinite(parsed) && parsed >= 0
      ? Math.floor(parsed)
      : undefined;
  }

  private toSpendingPlanRow(plan: SpendingPlanSummary): SpendingPlanRow {
    return {
      clientAuth: plan.clientAuthName ?? '',
      consumerName: plan.participantName ?? '',
      medicaid: plan.uciId ?? '',
      consumerPPLId: plan.pplId ?? '',
      serviceCoordinator: plan.serviceCoordinator ?? '',
      orgName: plan.regionalCenter ?? '',
      authorized: this.money(this.toNumber(plan.authUnit)),
      term: this.formatTerm(plan.planStartDate, plan.planEndDate),
      detailRows: [],
      detailsLoaded: false,
      detailsLoading: false,
      detailsError: false,
      expanded: false,
    };
  }

  /**
   * Authorizations, allocations and service details of one plan, fetched the first time
   * it is expanded — the plan list itself carries none of them.
   */
  private loadDetailRows(plan: SpendingPlanRow) {
    if (plan.detailsLoading) return;

    plan.detailsLoading = true;
    plan.detailsError = false;

    this.authorizationService
      .getSpendingPlanDetails(this.programName, plan.consumerPPLId, plan.clientAuth, plan.serviceCoordinator, plan.orgName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          const records: AuthorizationRecord[] = data?.responsedata ?? [];
          plan.detailRows = records.flatMap(auth => this.buildAuthRows(auth));
          plan.detailsLoaded = true;
          plan.detailsLoading = false;
        },
        error: () => {
          plan.detailRows = [];
          plan.detailsLoading = false;
          plan.detailsError = true;
        },
      });
  }

  /** Retry link inside an expanded plan whose detail call failed. */
  reloadDetails(plan: SpendingPlanRow) {
    this.loadDetailRows(plan);
  }

  /**
   * Emits the rows for one authorization, with the rowspans that merge its cells.
   * An authorization with no allocations, or an allocation with no service details,
   * still emits one row so incomplete setups stay visible.
   */
  private buildAuthRows(auth: AuthorizationRecord): DetailRow[] {
    const authCell = this.toAuthCell(auth);
    const providerAuths = auth.authProviders ?? [];
    const rows: DetailRow[] = [];

    if (providerAuths.length === 0) {
      rows.push({
        auth: authCell,
        authRowSpan: 1,
        allocation: this.placeholderAllocation(),
        allocationRowSpan: 1,
        serviceDetail: this.placeholderServiceDetail(),
        isAllocationEnd: true,
      });
      return rows;
    }

    // Deactivated service details are dropped here rather than in the loop below, so the
    // rowspans count only the rows that are actually rendered.
    const allocationDetails = providerAuths.map(
      providerAuth => (providerAuth.serviceDetails ?? [])
    );
    // Each allocation occupies one row per service detail, or a single row when it has
    // none — the authorization spans the sum of those.
    const allocationSpans = allocationDetails.map(details => Math.max(1, details.length));
    const authRowSpan = allocationSpans.reduce((sum, span) => sum + span, 0);

    let isFirstRowOfAuth = true;

    providerAuths.forEach((providerAuth, allocationIndex) => {
      const allocationCell = this.toAllocationCell(providerAuth);
      const allocationRowSpan = allocationSpans[allocationIndex];
      const serviceDetails: (ServiceDetailRecord | null)[] = allocationDetails[allocationIndex].length
        ? allocationDetails[allocationIndex]
        : [null];

      serviceDetails.forEach((serviceDetail, serviceDetailIndex) => {
        rows.push({
          auth: isFirstRowOfAuth ? authCell : null,
          authRowSpan: isFirstRowOfAuth ? authRowSpan : 0,
          allocation: serviceDetailIndex === 0 ? allocationCell : null,
          allocationRowSpan: serviceDetailIndex === 0 ? allocationRowSpan : 0,
          serviceDetail: serviceDetail
            ? this.toServiceDetailCell(serviceDetail)
            : this.placeholderServiceDetail(),
          isAllocationEnd: serviceDetailIndex === serviceDetails.length - 1,
        });
        isFirstRowOfAuth = false;
      });
    });

    return rows;
  }

  private toAuthCell(auth: AuthorizationRecord): AuthCell {
    return {
      authorizationId: auth.tasAuthorizationDetailId ?? 0,
      codeDesc: [auth.serviceCode, auth.serviceCodeDescription].filter(Boolean).join(' - '),
      serviceDescription: auth.serviceDescription ?? '',
      sourceValue: auth.sourceValue ?? '',
      term: this.formatTerm(auth.fromDate, auth.toDate),
      authorized: this.money(this.toNumber(auth.authorizedDollar)),
      spent: this.money(this.toNumber(auth.spentDollar)),
      remaining: this.money(this.toNumber(auth.remainingDollar)),
      isDeactivated: auth.isDeleted === 'Y' || auth.isDeleted === true,
    };
  }

  private toAllocationCell(providerAuth: ProviderAllocationRecord): AllocationCell {
    // An allocation held for a provider yet to be named comes back with no provider id —
    // the allocation grid shows those as TBD.
    const isUnassigned = !providerAuth.providerId || !providerAuth.providerPplId;

    return {
      authorizationProviderId: providerAuth.authorizationProviderId ?? null,
      pplProviderId: isUnassigned ? 'TBD' : providerAuth.providerPplId ?? '',
      providerName: providerAuth.providerName ?? '',
      term: this.formatTerm(providerAuth.startDate, providerAuth.endDate),
      allocated: this.money(this.toNumber(providerAuth.providerAllocatedDollar)),
      used: this.money(this.toNumber(providerAuth.providerUsedDollar)),
      remaining: this.money(this.toNumber(providerAuth.providerRemainingDollar)),
      isUnassigned,
      isActive: providerAuth.isActive !== false,
      isPlaceholder: false,
    };
  }

  private toServiceDetailCell(serviceDetail: ServiceDetailRecord): ServiceDetailCell {
    return {
      serviceDetail: serviceDetail.serviceDetail ?? '',
      frequencyLabel: getServiceFrequencyLabel(serviceDetail.frequency),
      units: serviceDetail.units === null || serviceDetail.units === undefined
        ? ''
        : String(serviceDetail.units),
      priceDisplay: formatServicePrice(serviceDetail.price),
      isActive: serviceDetail.isActive !== false,
      isPlaceholder: false,
    };
  }

  private placeholderAllocation(): AllocationCell {
    return {
      authorizationProviderId: null,
      pplProviderId: 'No provider allocation',
      providerName: '',
      term: '',
      allocated: '',
      used: '',
      remaining: '',
      isUnassigned: false,
      isActive: false,
      isPlaceholder: true,
    };
  }

  private placeholderServiceDetail(): ServiceDetailCell {
    return {
      serviceDetail: 'No service detail',
      frequencyLabel: '',
      units: '',
      priceDisplay: '',
      isActive: false,
      isPlaceholder: true,
    };
  }

  private toNumber(value: any): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private money(value: number): string {
    return value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  private formatTerm(startDate?: string, endDate?: string): string {
    if (!startDate && !endDate) return '';
    return `${this.formatDate(startDate)} – ${this.formatDate(endDate)}`;
  }

  /**
   * The plan list returns MM/DD/YYYY and the detail API returns YYYY-MM-DD; both are
   * shown as MM/DD/YYYY. Parsed by parts rather than `new Date`, so an ISO date is not
   * pulled back a day by the browser's timezone.
   */
  private formatDate(date?: string): string {
    if (!date) return '—';
    const parsed = this.dateTimeFormatter.formatStringToDate(date);
    return Number.isNaN(parsed.getTime()) ? date : this.dateTimeFormatter.formatDateToString(parsed);
  }

  private setPageAccess() {
    const authConfig = this.allowListStore.getConfig();
    if (authConfig && !authConfig.pages[this.pageId]) {
      this.isReadOnly = true;
    }
    this.isPageRestrictionLoaded = true;
  }
}
